import { memo } from 'react';

import { CircleCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const MessageAreaPrologue = ({
  conversation,
  reopenConversation,
  closeConversation,
  isExpired,
  isResolved,
  isSpam,
  isTrash,
  isExpiredAndResolved,
  isOpen,
}) => {
  const { t } = useTranslation();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  const getConditionalContent = () => {
    if (isExpiredAndResolved) {
      return (
        <>
          <span className="flex items-center text-sm leading-5 text-foreground">
            {t('conversation.resolved')}
          </span>
          <CircleCheckIcon size={16} className="mr-2 ml-auto text-success" />
        </>
      );
    } else if (isResolved || isSpam || isTrash) {
      const canReopen = canWrite && !isSpam && !isTrash;
      return (
        <>
          <span className="flex items-center text-sm leading-5 text-foreground">
            {t('conversation.resolved')}
          </span>
          {canReopen ? (
            <Button
              variant="link"
              size="sm"
              className="ml-auto text-primary"
              onClick={reopenConversation}
            >
              {t('conversation.reopen')}
            </Button>
          ) : (
            <CircleCheckIcon size={16} className="mr-2 ml-auto text-success" />
          )}
        </>
      );
    } else if (isExpired && !isResolved && !isSpam && !isTrash) {
      return (
        <>
          <span className="flex items-center text-sm leading-5 text-foreground">
            {t('conversation.expired')}
          </span>
          {canWrite && (
            <Button
              variant="link"
              size="sm"
              className="ml-auto text-primary"
              onClick={closeConversation}
            >
              {t('conversation.resolve')}
            </Button>
          )}
        </>
      );
    } else if (isOpen) {
      return (
        <>
          <span className="flex items-center text-sm leading-5 text-foreground">
            {t('conversation.open')}
          </span>
          {canWrite && (
            <Button
              variant="link"
              size="sm"
              className="ml-auto text-primary"
              onClick={closeConversation}
            >
              {t('conversation.resolve')}
            </Button>
          )}
        </>
      );
    }
  };

  if (!conversation) {
    return null;
  }

  return (
    <div
      className={cn(
        'z-1 flex h-10 items-center bg-muted/50 pr-2 pl-4 shadow-sm',
        isExpired && !isExpiredAndResolved && 'bg-selected-soft',
        (isResolved || isSpam || isTrash) && 'bg-success-soft',
        isOpen && !isExpired && 'bg-info-soft'
      )}
    >
      {getConditionalContent()}
    </div>
  );
};

export default memo(MessageAreaPrologue);
